import { useEffect, useMemo, useState, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet as SheetRoot, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ShoppingBag, Minus, Plus, ArrowLeft, X, Check, Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// -------------------- Cart Context --------------------
const CartCtx = createContext(null);
const useCart = () => useContext(CartCtx);

function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [], subtotal: 0, item_count: 0 });
  const [cartOpen, setCartOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const addItem = useCallback(async (sync_variant_id, quantity = 1) => {
    const { data } = await api.post("/cart/items", { sync_variant_id, quantity });
    setCart(data);
    setCartOpen(true);
    return data;
  }, []);

  const updateItem = useCallback(async (sync_variant_id, quantity) => {
    const { data } = await api.put("/cart/items", { sync_variant_id, quantity });
    setCart(data);
  }, []);

  const removeItem = useCallback(async (sync_variant_id) => {
    const { data } = await api.delete(`/cart/items/${sync_variant_id}`);
    setCart(data);
  }, []);

  return (
    <CartCtx.Provider value={{ cart, refresh, addItem, updateItem, removeItem, cartOpen, setCartOpen }}>
      {children}
    </CartCtx.Provider>
  );
}

// -------------------- Layout --------------------
function Header() {
  const { cart, setCartOpen } = useCart();
  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-neutral-200">
      <div className="mx-auto max-w-6xl px-5 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2">
          <span className="font-black tracking-tighter text-2xl">RENT DUE</span>
          <span className="hidden sm:inline text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-l border-neutral-300 pl-2 ml-1">
            Saginaw · Est. 2024
          </span>
        </Link>
        <button
          data-testid="open-cart-btn"
          onClick={() => setCartOpen(true)}
          className="relative inline-flex items-center gap-2 px-3 py-2 rounded-full hover:bg-neutral-100 transition-colors"
        >
          <ShoppingBag className="w-5 h-5" />
          <span className="text-sm font-medium">Cart</span>
          {cart.item_count > 0 && (
            <span
              data-testid="cart-count-badge"
              className="absolute -top-1 -right-1 bg-black text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
            >
              {cart.item_count}
            </span>
          )}
        </button>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-neutral-200 mt-24">
      <div className="mx-auto max-w-6xl px-5 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-neutral-500">
        <div>
          <div className="font-black text-base text-black tracking-tight">RENT DUE</div>
          <div>© {new Date().getFullYear()} Rent Due. All rights reserved.</div>
        </div>
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4" /> Drop shipped from our lead designers · Ships in 2–5 business days
        </div>
      </div>
    </footer>
  );
}

// -------------------- Reserve Hoodie Dialog --------------------
const HOODIE_COLORS = ["Black"];
const HOODIE_SIZES = ["S", "M", "L", "XL", "2XL", "3XL"];

function ReserveHoodieDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ email: "", name: "", color: "Black", size: "M", quantity: 1, note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const reset = () => { setForm({ email: "", name: "", color: "Black", size: "M", quantity: 1, note: "" }); setDone(false); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reservations", form);
      setDone(true);
      toast.success("Reservation saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save reservation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 300); }}>
      <DialogContent className="sm:max-w-md" data-testid="reserve-dialog">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-black tracking-tight">Reserve your Cotton Blend Hoodie</DialogTitle>
              <DialogDescription>
                Tell us your size + color. We&apos;ll hold one for you and email the moment the fall / winter drop lands. No payment now.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="reserve-form">
              <div>
                <Label className="text-xs">Email *</Label>
                <Input
                  type="email"
                  required
                  data-testid="reserve-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className="mt-1"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <Label className="text-xs">Name (optional)</Label>
                <Input
                  data-testid="reserve-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  className="mt-1"
                  placeholder="First name"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Color</Label>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {HOODIE_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      data-testid={`reserve-color-${c.replace(/\s+/g, '-')}`}
                      onClick={() => set("color", c)}
                      className={`px-4 py-2 text-sm border rounded-full transition-all ${
                        form.color === c ? "bg-black text-white border-black" : "bg-white text-black border-neutral-300 hover:border-black"
                      }`}
                    >{c}</button>
                  ))}
                  <span
                    data-testid="reserve-colors-soon"
                    className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 border border-dashed border-neutral-300 rounded-full px-3 py-1.5"
                  >
                    New colors coming soon
                  </span>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Size</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {HOODIE_SIZES.map(s => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`reserve-size-${s}`}
                      onClick={() => set("size", s)}
                      className={`min-w-[3rem] px-4 py-2 text-sm border rounded-full transition-all ${
                        form.size === s ? "bg-black text-white border-black" : "bg-white text-black border-neutral-300 hover:border-black"
                      }`}
                    >{s}</button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Quantity</Label>
                <div className="mt-2 inline-flex items-center border border-neutral-300 rounded-full">
                  <button
                    type="button"
                    data-testid="reserve-qty-dec"
                    onClick={() => set("quantity", Math.max(1, form.quantity - 1))}
                    className="px-4 py-2 hover:bg-neutral-100 rounded-l-full"
                  ><Minus className="w-3.5 h-3.5" /></button>
                  <span data-testid="reserve-qty" className="px-4 min-w-[2.5rem] text-center">{form.quantity}</span>
                  <button
                    type="button"
                    data-testid="reserve-qty-inc"
                    onClick={() => set("quantity", Math.min(10, form.quantity + 1))}
                    className="px-4 py-2 hover:bg-neutral-100 rounded-r-full"
                  ><Plus className="w-3.5 h-3.5" /></button>
                </div>
              </div>

              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Input
                  data-testid="reserve-note"
                  value={form.note}
                  onChange={(e) => set("note", e.target.value)}
                  className="mt-1"
                  placeholder="Anything you'd like us to know?"
                />
              </div>

              <DialogFooter className="mt-4">
                <Button
                  type="submit"
                  data-testid="reserve-submit"
                  disabled={submitting || !form.email}
                  className="w-full bg-black text-white hover:bg-neutral-800 rounded-full py-6"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {submitting ? "Reserving..." : "Reserve my hoodie"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div className="py-6 text-center" data-testid="reserve-success">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-black text-white mb-4">
              <Check className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold tracking-tight">You&apos;re on the list</h3>
            <p className="mt-2 text-sm text-neutral-600">
              We&apos;re holding a <strong>{form.color} · {form.size} × {form.quantity}</strong> for you.
              We&apos;ll email <strong>{form.email}</strong> the moment the Cotton Blend Hoodie drops.
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="mt-6 bg-black text-white hover:bg-neutral-800 rounded-full px-6"
              data-testid="reserve-close"
            >Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Home / Product List --------------------
function Home() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reserveOpen, setReserveOpen] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setProducts(data.items || []);
    }).catch(e => {
      console.error(e);
      toast.error("Failed to load products");
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="border-b border-neutral-200">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:py-24">
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-4">Streetwear · Saginaw</p>
          <h1 className="text-5xl sm:text-7xl font-black tracking-tighter leading-[0.95]">
            Pay rent. <br /><span className="italic font-serif font-light">Stay loud.</span>
          </h1>
          <p className="mt-6 text-neutral-600 max-w-xl">
            Limited-run hoodies, hats and tees. Premium print quality, drop shipped from our
            lead designers — fast.
          </p>
        </div>
      </section>

      {/* Coming Soon — Fall/Winter Cotton Blend Rent Due Hoodie */}
      <section className="border-b border-neutral-200 bg-neutral-900 text-white">
        <div className="mx-auto max-w-6xl px-5 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-4">New for fall / winter '26</p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
              Cotton Blend <span className="italic font-serif font-light">Rent Due Hoodie</span>
            </h2>
            <p className="mt-5 text-neutral-300 max-w-lg">
              Heavyweight cotton-blend fleece with a bold, oversized <span className="font-semibold text-white">RENT DUE</span> front hit.
              Built for cold Michigan mornings, layered up, or worn on its own.
              The everyday staple of the winter drop.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-neutral-200" data-testid="coming-soon-features">
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> 80/20 <strong>cotton-poly blend</strong> · brushed fleece interior</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Heavyweight 12 oz — built for winter</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Ribbed cuffs &amp; hem · kangaroo pocket</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Unisex sizing · S–3XL</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Launching in <strong>Black</strong> — new colors coming soon</li>
            </ul>
            <div className="mt-8">
              <button
                data-testid="open-reserve-btn"
                onClick={() => setReserveOpen(true)}
                className="inline-flex items-center gap-2 bg-white text-black rounded-full px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-neutral-200 transition-colors"
              >
                <span className="w-2 h-2 rounded-full bg-black animate-pulse" />
                Reserve yours
              </button>
              <p className="mt-3 text-xs text-neutral-400">
                No payment now — we&apos;ll email you the moment it drops with your reservation held.
              </p>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://customer-assets-0z36b82j.emergentagent.net/job_rent-payment-portal/artifacts/vw8op28j_DD583481-F9D2-4794-9E6C-DA11C2B3CE4E.png"
              alt="Cotton Blend Rent Due Hoodie — black heavyweight fleece with white RENT DUE front graphic"
              data-testid="coming-soon-hero-image"
              className="w-full h-auto rounded-lg shadow-lg bg-white"
            />
            <span className="absolute top-4 left-4 bg-white text-black text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-full">
              Coming Soon
            </span>
          </div>
        </div>
      </section>

      <ReserveHoodieDialog open={reserveOpen} onOpenChange={setReserveOpen} />

      {/* Grid */}
      <section className="mx-auto max-w-6xl px-5 py-12">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="text-2xl font-bold tracking-tight">The Collection</h2>
          <span className="text-sm text-neutral-500" data-testid="product-count">{products.length} pieces</span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => (
              <Skeleton key={i} className="aspect-square w-full" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map(p => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                data-testid={`product-card-${p.id}`}
                className="group block"
              >
                <div className="aspect-square bg-neutral-100 overflow-hidden">
                  <img
                    src={p.thumbnail_url}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <h3 className="font-medium text-neutral-900">{(p.name || "").trim()}</h3>
                  <span className="text-xs text-neutral-500">{p.variants} variant{p.variants !== 1 ? "s" : ""}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Retailer / Wholesale CTA */}
      <RetailerCTASection />
    </div>
  );
}

// -------------------- Retailer / Wholesale --------------------
const EXTENDED_COLORS = [
  "Bay Blue", "Fog White", "Charcoal", "Sunrise Orange",
  "Forest Green", "Storm Grey", "Cardinal Red", "Sand Tan",
  "Midnight Navy", "Sail Yellow", "Lakeshore Teal",
];
const PRODUCT_LINES = [
  "UV Hoodie (50+ SPF)", "Classic Hoodie", "Zip-Up Hoodie",
  "Hat / Dad Hat", "Snap Back", "Tee",
];

function RetailerCTASection() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <section className="bg-black text-white">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-20 grid md:grid-cols-[1.2fr_auto] gap-8 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-3">
              For retailers · Wholesale &amp; custom orders
            </p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
              Stock Rent Due in <span className="italic font-serif font-light">your store.</span>
            </h2>
            <p className="mt-5 text-neutral-300 max-w-xl">
              Boutique, sporting goods shop, resort, festival, or event vendor? We offer
              <strong className="text-white"> extended colorways</strong>, volume pricing,
              and custom co-branded designs beyond what&apos;s on the retail site. Tell us what you need
              and we&apos;ll get back to you within 2 business days.
            </p>
            <ul className="mt-5 text-sm text-neutral-300 grid grid-cols-2 gap-y-2 gap-x-6 max-w-lg">
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> 11+ extended colors</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Volume pricing</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Co-branded runs</li>
              <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Fast turnaround</li>
            </ul>
          </div>
          <button
            data-testid="open-retailer-btn"
            onClick={() => setOpen(true)}
            className="inline-flex items-center justify-center gap-2 bg-white text-black rounded-full px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-neutral-200 transition-colors whitespace-nowrap"
          >
            Retailer? Click here
          </button>
        </div>
      </section>
      <RetailerInquiryDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

function RetailerInquiryDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    business_type: "", website: "", location: "",
    expected_units: "", timeline: "", message: "",
    colors_of_interest: [], products_of_interest: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleArr = (key, val) => setForm(f => ({
    ...f,
    [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val],
  }));
  const reset = () => {
    setForm({
      business_name: "", contact_name: "", email: "", phone: "",
      business_type: "", website: "", location: "",
      expected_units: "", timeline: "", message: "",
      colors_of_interest: [], products_of_interest: [],
    });
    setDone(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        expected_units: form.expected_units ? parseInt(form.expected_units, 10) || null : null,
      };
      await api.post("/retailer-inquiries", payload);
      setDone(true);
      toast.success("Inquiry sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send inquiry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 300); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="retailer-dialog">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-black tracking-tight">Retailer / Wholesale Inquiry</DialogTitle>
              <DialogDescription>
                Tell us about your business and what you need. We&apos;ll respond within 2 business days.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="retailer-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Business name *</Label>
                  <Input required data-testid="ret-business" value={form.business_name}
                    onChange={(e) => set("business_name", e.target.value)} className="mt-1" placeholder="Bay Bait & Tackle" />
                </div>
                <div>
                  <Label className="text-xs">Contact name *</Label>
                  <Input required data-testid="ret-contact" value={form.contact_name}
                    onChange={(e) => set("contact_name", e.target.value)} className="mt-1" placeholder="Your name" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Email *</Label>
                  <Input required type="email" data-testid="ret-email" value={form.email}
                    onChange={(e) => set("email", e.target.value)} className="mt-1" placeholder="you@business.com" />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input data-testid="ret-phone" value={form.phone}
                    onChange={(e) => set("phone", e.target.value)} className="mt-1" placeholder="(555) 555-5555" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Business type</Label>
                  <select
                    data-testid="ret-type"
                    value={form.business_type}
                    onChange={(e) => set("business_type", e.target.value)}
                    className="mt-1 flex h-9 w-full rounded-md border border-neutral-300 bg-white px-3 py-1 text-sm shadow-sm"
                  >
                    <option value="">Select…</option>
                    <option value="boutique">Boutique</option>
                    <option value="sporting_goods">Sporting goods / Bait & tackle</option>
                    <option value="resort">Resort / Marina</option>
                    <option value="event_vendor">Event / Festival vendor</option>
                    <option value="fishing_charter">Fishing charter</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Location (city, state)</Label>
                  <Input data-testid="ret-location" value={form.location}
                    onChange={(e) => set("location", e.target.value)} className="mt-1" placeholder="Bay City, MI" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Website</Label>
                  <Input data-testid="ret-website" value={form.website}
                    onChange={(e) => set("website", e.target.value)} className="mt-1" placeholder="https://" />
                </div>
                <div>
                  <Label className="text-xs">Estimated units</Label>
                  <Input type="number" min="1" data-testid="ret-units" value={form.expected_units}
                    onChange={(e) => set("expected_units", e.target.value)} className="mt-1" placeholder="e.g. 50" />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Products of interest</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {PRODUCT_LINES.map(p => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`ret-product-${p.replace(/[^a-zA-Z]/g, '-')}`}
                      onClick={() => toggleArr("products_of_interest", p)}
                      className={`px-3 py-1.5 text-xs border rounded-full transition-all ${
                        form.products_of_interest.includes(p)
                          ? "bg-black text-white border-black"
                          : "bg-white text-black border-neutral-300 hover:border-black"
                      }`}
                    >{p}</button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Extended colors of interest</Label>
                <p className="text-xs text-neutral-500 mb-2">Retail site shows Bay Blue &amp; Fog White. Full retailer palette:</p>
                <div className="flex flex-wrap gap-2">
                  {EXTENDED_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      data-testid={`ret-color-${c.replace(/\s+/g, '-')}`}
                      onClick={() => toggleArr("colors_of_interest", c)}
                      className={`px-3 py-1.5 text-xs border rounded-full transition-all ${
                        form.colors_of_interest.includes(c)
                          ? "bg-black text-white border-black"
                          : "bg-white text-black border-neutral-300 hover:border-black"
                      }`}
                    >{c}</button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs">Timeline</Label>
                <select
                  data-testid="ret-timeline"
                  value={form.timeline}
                  onChange={(e) => set("timeline", e.target.value)}
                  className="mt-1 flex h-9 w-full rounded-md border border-neutral-300 bg-white px-3 py-1 text-sm shadow-sm"
                >
                  <option value="">Select…</option>
                  <option value="ASAP">ASAP</option>
                  <option value="1-3 months">1–3 months</option>
                  <option value="3-6 months">3–6 months</option>
                  <option value="Just exploring">Just exploring</option>
                </select>
              </div>

              <div>
                <Label className="text-xs">Message</Label>
                <textarea
                  data-testid="ret-message"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  className="mt-1 flex min-h-[80px] w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm shadow-sm"
                  placeholder="Anything else we should know? Custom co-branding ideas, delivery windows, etc."
                />
              </div>

              <DialogFooter>
                <Button
                  type="submit"
                  data-testid="ret-submit"
                  disabled={submitting || !form.business_name || !form.contact_name || !form.email}
                  className="w-full bg-black text-white hover:bg-neutral-800 rounded-full py-6"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {submitting ? "Sending..." : "Send inquiry"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div className="py-8 text-center" data-testid="ret-success">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-black text-white mb-4">
              <Check className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold tracking-tight">Inquiry received</h3>
            <p className="mt-2 text-sm text-neutral-600 max-w-md mx-auto">
              Thanks {form.contact_name || "there"} — we&apos;ll reach out to <strong>{form.email}</strong> within 2 business days
              to talk pricing and next steps for <strong>{form.business_name}</strong>.
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="mt-6 bg-black text-white hover:bg-neutral-800 rounded-full px-6"
              data-testid="ret-close"
            >Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Product Detail --------------------
function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      if (data.sizes.length === 1) setSelectedSize(data.sizes[0]);
      if (data.colors.length === 1) setSelectedColor(data.colors[0]);
    }).catch(e => {
      toast.error("Couldn't load product");
      console.error(e);
    }).finally(() => setLoading(false));
  }, [id]);

  const selectedVariant = useMemo(() => {
    if (!product) return null;
    return product.variants.find(v =>
      (product.sizes.length === 0 || v.size === selectedSize) &&
      (product.colors.length === 0 || v.color === selectedColor)
    ) || null;
  }, [product, selectedSize, selectedColor]);

  const previewImage = useMemo(() => {
    if (!product) return null;
    if (selectedVariant?.image) return selectedVariant.image;
    return product.thumbnail_url || product.images?.[0]?.url;
  }, [product, selectedVariant]);

  const onAddToCart = async () => {
    if (!selectedVariant) {
      toast.error("Please choose " + (product.sizes.length && !selectedSize ? "a size" : "a color"));
      return;
    }
    setAdding(true);
    try {
      await addItem(selectedVariant.id, quantity);
      toast.success("Added to cart");
    } catch (e) {
      toast.error("Couldn't add to cart");
      console.error(e);
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl px-5 py-10 grid md:grid-cols-2 gap-10">
        <Skeleton className="aspect-square w-full" />
        <div className="space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-6 w-1/4" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  if (!product) {
    return <div className="mx-auto max-w-6xl px-5 py-20 text-center text-neutral-500">Product not found.</div>;
  }

  const needsSize = product.sizes.length > 0;
  const needsColor = product.colors.length > 0;

  return (
    <div className="mx-auto max-w-6xl px-5 py-10">
      <Link to="/" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-black mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to shop
      </Link>

      <div className="grid md:grid-cols-2 gap-10">
        {/* Image */}
        <div>
          <div className="aspect-square bg-neutral-100 overflow-hidden">
            {previewImage && (
              <img
                key={previewImage}
                src={previewImage}
                alt={product.name}
                data-testid="product-image"
                className="w-full h-full object-cover"
              />
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {product.images.slice(0, 10).map((img, i) => (
                <button
                  key={i}
                  onClick={() => img.color && setSelectedColor(img.color)}
                  className={`aspect-square overflow-hidden border-2 transition-colors ${
                    selectedColor === img.color ? "border-black" : "border-transparent"
                  }`}
                >
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info / Options */}
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="product-title">
            {product.name}
          </h1>
          <div className="mt-3 text-2xl font-semibold" data-testid="product-price">
            ${(selectedVariant?.retail_price ?? product.min_price).toFixed(2)}
          </div>

          {needsColor && (
            <div className="mt-8">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Color</Label>
              <div className="mt-3 flex flex-wrap gap-2">
                {product.colors.map(c => (
                  <button
                    key={c}
                    data-testid={`color-option-${c}`}
                    onClick={() => setSelectedColor(c)}
                    className={`px-4 py-2 text-sm border rounded-full transition-all ${
                      selectedColor === c
                        ? "bg-black text-white border-black"
                        : "bg-white text-black border-neutral-300 hover:border-black"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {needsSize && (
            <div className="mt-6">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Size</Label>
              <div className="mt-3 flex flex-wrap gap-2">
                {product.sizes.map(s => (
                  <button
                    key={s}
                    data-testid={`size-option-${s}`}
                    onClick={() => setSelectedSize(s)}
                    className={`min-w-[3rem] px-4 py-2 text-sm border rounded-full transition-all ${
                      selectedSize === s
                        ? "bg-black text-white border-black"
                        : "bg-white text-black border-neutral-300 hover:border-black"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Quantity</Label>
            <div className="mt-3 inline-flex items-center border border-neutral-300 rounded-full">
              <button
                data-testid="qty-decrement"
                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                className="px-4 py-2 hover:bg-neutral-100 rounded-l-full"
              ><Minus className="w-3.5 h-3.5" /></button>
              <span data-testid="qty-value" className="px-4 min-w-[2.5rem] text-center">{quantity}</span>
              <button
                data-testid="qty-increment"
                onClick={() => setQuantity(q => q + 1)}
                className="px-4 py-2 hover:bg-neutral-100 rounded-r-full"
              ><Plus className="w-3.5 h-3.5" /></button>
            </div>
          </div>

          <button
            data-testid="add-to-cart-btn"
            onClick={onAddToCart}
            disabled={adding || (needsSize && !selectedSize) || (needsColor && !selectedColor)}
            className="mt-8 w-full bg-black text-white py-4 text-sm font-semibold uppercase tracking-[0.2em] rounded-full hover:bg-neutral-800 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {adding ? "Adding..." : "Add to cart"}
          </button>

          <div className="mt-6 p-4 border border-neutral-200 rounded-lg bg-neutral-50">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Check className="w-4 h-4" /> Drop shipped from our lead designers
            </div>
            <p className="text-xs text-neutral-500 mt-1">Premium print quality · Ships in 2–5 business days</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// -------------------- Cart Drawer --------------------
function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateItem, removeItem } = useCart();
  const navigate = useNavigate();

  return (
    <SheetRoot open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle>Your Cart</SheetTitle>
          <SheetDescription className="sr-only">Items in your shopping cart</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 -mx-6 px-6">
          {cart.items.length === 0 ? (
            <div className="text-center text-neutral-500 py-20" data-testid="cart-empty">
              Your cart is empty.
            </div>
          ) : (
            <div className="space-y-4">
              {cart.items.map(it => (
                <div key={it.sync_variant_id} data-testid={`cart-item-${it.sync_variant_id}`} className="flex gap-3 border-b border-neutral-100 pb-4">
                  <div className="w-20 h-20 bg-neutral-100 flex-shrink-0 overflow-hidden">
                    {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{it.name}</div>
                    <div className="text-xs text-neutral-500">
                      {[it.color, it.size].filter(Boolean).join(" · ")}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center border border-neutral-300 rounded-full text-sm">
                        <button onClick={() => updateItem(it.sync_variant_id, it.quantity - 1)} className="px-2 py-1"><Minus className="w-3 h-3" /></button>
                        <span className="px-2">{it.quantity}</span>
                        <button onClick={() => updateItem(it.sync_variant_id, it.quantity + 1)} className="px-2 py-1"><Plus className="w-3 h-3" /></button>
                      </div>
                      <div className="text-sm font-medium">${(it.retail_price * it.quantity).toFixed(2)}</div>
                    </div>
                  </div>
                  <button onClick={() => removeItem(it.sync_variant_id)} className="text-neutral-400 hover:text-black self-start" data-testid={`remove-${it.sync_variant_id}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <SheetFooter className="border-t border-neutral-200 pt-4 mt-auto">
          <div className="w-full">
            <div className="flex justify-between mb-4">
              <span className="text-sm text-neutral-500">Subtotal</span>
              <span className="font-semibold" data-testid="cart-subtotal">${cart.subtotal.toFixed(2)}</span>
            </div>
            <Button
              data-testid="checkout-btn"
              disabled={cart.items.length === 0}
              onClick={() => { setCartOpen(false); navigate("/checkout"); }}
              className="w-full bg-black text-white hover:bg-neutral-800 rounded-full py-6"
            >
              Checkout
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </SheetRoot>
  );
}

// -------------------- Checkout --------------------
function Checkout() {
  const { cart } = useCart();
  const [form, setForm] = useState({
    name: "", address1: "", address2: "", city: "", state_code: "", country_code: "US", zip: "", email: "", phone: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (cart.items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/session", {
        recipient: form,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url; // redirect to Stripe-hosted checkout
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Couldn't start checkout";
      toast.error(msg);
      console.error(err);
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-10">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-black mb-6">
        <ArrowLeft className="w-4 h-4" /> Continue shopping
      </Link>
      <h1 className="text-3xl font-bold tracking-tight mb-8">Checkout</h1>

      <div className="grid md:grid-cols-[1fr_360px] gap-10">
        <form onSubmit={submit} className="space-y-5" data-testid="checkout-form">
          <h2 className="font-semibold">Shipping address</h2>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Full name" required value={form.name} onChange={setField("name")} testid="ck-name" />
            <FormField label="Email" type="email" required value={form.email} onChange={setField("email")} testid="ck-email" />
          </div>
          <FormField label="Address" required value={form.address1} onChange={setField("address1")} testid="ck-address1" />
          <FormField label="Apt, suite (optional)" value={form.address2} onChange={setField("address2")} testid="ck-address2" />
          <div className="grid grid-cols-2 gap-3">
            <FormField label="City" required value={form.city} onChange={setField("city")} testid="ck-city" />
            <FormField label="State (2-letter)" required value={form.state_code} onChange={setField("state_code")} testid="ck-state" maxLength={2} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="ZIP" required value={form.zip} onChange={setField("zip")} testid="ck-zip" />
            <FormField label="Country (2-letter)" required value={form.country_code} onChange={setField("country_code")} testid="ck-country" maxLength={2} />
          </div>
          <FormField label="Phone (optional)" value={form.phone} onChange={setField("phone")} testid="ck-phone" />

          <Button
            type="submit"
            data-testid="pay-with-stripe-btn"
            disabled={submitting || cart.items.length === 0}
            className="w-full bg-black text-white hover:bg-neutral-800 rounded-full py-6"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {submitting ? "Redirecting to Stripe..." : "Pay with Stripe"}
          </Button>
          <p className="text-xs text-neutral-500">
            You&apos;ll be redirected to Stripe to complete payment securely. After payment, your order is automatically sent to our lead designers for fulfillment.
          </p>
        </form>

        <aside className="bg-neutral-50 border border-neutral-200 rounded-xl p-5 h-fit">
          <h2 className="font-semibold mb-4">Order summary</h2>
          {cart.items.map(it => (
            <div key={it.sync_variant_id} className="flex gap-3 py-3 border-b border-neutral-200 last:border-b-0">
              <div className="w-14 h-14 bg-white flex-shrink-0 overflow-hidden">
                {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 text-sm">
                <div className="font-medium truncate">{it.name}</div>
                <div className="text-xs text-neutral-500">
                  {[it.color, it.size].filter(Boolean).join(" · ")} × {it.quantity}
                </div>
              </div>
              <div className="text-sm">${(it.retail_price * it.quantity).toFixed(2)}</div>
            </div>
          ))}
          <div className="mt-4 pt-4 border-t border-neutral-200 flex justify-between font-semibold">
            <span>Subtotal</span>
            <span data-testid="checkout-subtotal">${cart.subtotal.toFixed(2)}</span>
          </div>
          <p className="text-xs text-neutral-500 mt-2">Shipping calculated at fulfillment.</p>
        </aside>
      </div>
    </div>
  );
}

function FormField({ label, required, value, onChange, testid, type = "text", maxLength }) {
  return (
    <div>
      <Label className="text-xs">{label}{required && " *"}</Label>
      <Input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        className="mt-1"
      />
    </div>
  );
}

// -------------------- Order Confirmation --------------------
function OrderConfirmation() {
  const { refresh } = useCart();
  const [state, setState] = useState({ loading: true, paymentStatus: null, order: null, error: null });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState({ loading: false, paymentStatus: null, order: null, error: "Missing session id" });
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const MAX = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ loading: false, paymentStatus: "paid", order: data.order, error: null });
          refresh();
          return;
        }
        if (data.status === "expired") {
          setState({ loading: false, paymentStatus: "expired", order: data.order, error: "Payment session expired" });
          return;
        }
        if (attempts >= MAX) {
          setState({ loading: false, paymentStatus: data.payment_status || "pending", order: data.order, error: "Still processing — check back shortly" });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, paymentStatus: null, order: null, error: e.response?.data?.detail || "Couldn't verify payment" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [refresh]);

  if (state.loading) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="payment-verifying">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-neutral-400" />
        <h1 className="mt-6 text-2xl font-bold tracking-tight">Verifying your payment…</h1>
        <p className="mt-2 text-sm text-neutral-500">Hang tight, this takes a few seconds.</p>
      </div>
    );
  }

  const success = state.paymentStatus === "paid";

  return (
    <div className="mx-auto max-w-2xl px-5 py-20 text-center">
      <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${success ? "bg-black text-white" : "bg-amber-100 text-amber-700"} mb-6`}>
        {success ? <Check className="w-6 h-6" /> : <X className="w-6 h-6" />}
      </div>
      <h1 className="text-3xl font-bold tracking-tight" data-testid="confirmation-title">
        {success ? "Payment confirmed" : "Payment not completed"}
      </h1>
      <p className="mt-3 text-neutral-600" data-testid="confirmation-message">
        {success
          ? "Your order has been sent to our lead designers for production and shipping."
          : (state.error || "Your payment didn't go through. Your cart is still saved — try again.")}
      </p>
      {state.order?.printful_order_id && (
        <p className="mt-2 text-sm text-neutral-500">
          Order #{state.order.printful_order_id} · ${state.order.grand_total?.toFixed(2)}
        </p>
      )}
      <div className="mt-10 flex gap-3 justify-center">
        {!success && (
          <Link to="/checkout" className="inline-block px-6 py-3 rounded-full border border-black text-black text-sm font-semibold uppercase tracking-[0.2em]">
            Try again
          </Link>
        )}
        <Link to="/" className="inline-block px-6 py-3 rounded-full bg-black text-white text-sm font-semibold uppercase tracking-[0.2em]">
          Keep shopping
        </Link>
      </div>
    </div>
  );
}

// -------------------- App --------------------
function App() {
  useEffect(() => {
    document.title = "Rent Due Clothing";
    // Guard against platform scripts overriding the title after we set it.
    const observer = new MutationObserver(() => {
      if (document.title !== "Rent Due Clothing") document.title = "Rent Due Clothing";
    });
    const titleEl = document.querySelector("title");
    if (titleEl) observer.observe(titleEl, { childList: true });
    return () => observer.disconnect();
  }, []);
  return (
    <div className="App min-h-screen bg-white text-neutral-900">
      <BrowserRouter>
        <CartProvider>
          <Header />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
          </Routes>
          <CartDrawer />
          <Footer />
          <Toaster position="bottom-right" />
        </CartProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
